/**
 * Account Service API.
 * 
 * High-level service for account/profile management.
 * Uses localStorage-backed storage (MVP).
 */

import type { Account, Profile, ProfileType, Visibility, Workspace, WorkspaceMember, MembershipStatus } from '@yada/types';
import {
  getAccount as getAccountStorage,
  setAccount as setAccountStorage,
  getProfile as getProfileStorage,
  getProfiles as getProfilesStorage,
  getProfilesForAccount,
  setProfile as setProfileStorage,
  setProfiles as setProfilesStorage,
  reorderProfiles as reorderProfilesStorage,
  removeProfile as removeProfileStorage,
  getActiveProfileId as getActiveProfileIdStorage,
  setActiveProfileId as setActiveProfileIdStorage,
  getWorkspace as getWorkspaceStorage,
  getWorkspaces as getWorkspacesStorage,
  setWorkspace as setWorkspaceStorage,
  removeWorkspace as removeWorkspaceStorage,
  getWorkspaceMembers,
  setWorkspaceMember,
  removeWorkspaceMember as removeWorkspaceMemberStorage,
} from './accounts';
import { generateId } from '@yada/storage';

/**
 * Get the current account.
 */
export async function getCurrentAccount(): Promise<Account | null> {
  return getAccountStorage();
}

/**
 * Get the current active profile.
 */
export async function getCurrentProfile(): Promise<Profile | null> {
  const profileId = await getActiveProfileId();
  if (!profileId) return null;
  return getProfileStorage(profileId);
}

/**
 * Get all profiles for the current account.
 */
export async function getProfilesForCurrentAccount(): Promise<Profile[]> {
  const account = await getCurrentAccount();
  if (!account) return [];
  return getProfilesForAccount(account.id);
}

/**
 * Create a new profile.
 */
export async function createProfile(data: {
  name: string;
  profileType: ProfileType;
  visibility?: Visibility;
  metadata?: Record<string, unknown>;
}): Promise<Profile> {
  let account = await getCurrentAccount();
  if (!account) {
    // Auto-create account if missing (dev mode)
    const devAccount: Account = {
      id: generateId(),
      createdAt: new Date(),
      recoveryMode: 'standard',
      securityPrefs: {},
      enabledApps: [],
    };
    await setAccountStorage(devAccount);
    account = devAccount;
  }

  const now = new Date();
  const profile: Profile = {
    id: generateId(),
    accountId: account?.id || null,
    profileType: data.profileType,
    name: data.name,
    verificationLevel: 0,
    visibility: data.visibility || 'public',
    metadata: data.metadata || {},
    createdAt: now,
    updatedAt: now,
  };

  await setProfileStorage(profile);
  
  // If this is the first profile, or if it's an internal profile, set it as active
  const profiles = await getProfilesForCurrentAccount();
  if (profiles.length === 0 || profile.profileType === 'internal') {
    await setActiveProfileId(profile.id);
  }

  return profile;
}

/**
 * Update an existing profile.
 */
export async function updateProfile(
  id: string,
  updates: {
    name?: string;
    visibility?: Visibility;
    metadata?: Record<string, unknown>;
  }
): Promise<Profile> {
  const profile = await getProfileStorage(id);
  if (!profile) {
    throw new Error(`Profile not found: ${id}`);
  }

  const updated: Profile = {
    ...profile,
    ...updates,
    updatedAt: new Date(),
  };

  await setProfileStorage(updated);
  return updated;
}

/**
 * Delete a profile.
 */
export async function deleteProfile(id: string): Promise<void> {
  const activeProfileId = await getActiveProfileId();
  await removeProfileStorage(id);
  
  // If we deleted the active profile, clear it
  if (activeProfileId === id) {
    await setActiveProfileId(null);
  }
}

/**
 * Set the active profile.
 */
export async function setActiveProfile(profileId: string): Promise<void> {
  const profile = await getProfileStorage(profileId);
  if (!profile) {
    throw new Error(`Profile not found: ${profileId}`);
  }
  await setActiveProfileIdStorage(profileId);
}

export async function setActiveProfileId(profileId: string | null): Promise<void> {
  await setActiveProfileIdStorage(profileId);
}

export async function getActiveProfileId(): Promise<string | null> {
  return getActiveProfileIdStorage();
}

export async function setProfile(profile: Profile): Promise<void> {
  await setProfileStorage(profile);
}

export async function setProfiles(profiles: Profile[]): Promise<void> {
  await setProfilesStorage(profiles);
}

export async function reorderProfiles(orderedIds: string[]): Promise<void> {
  await reorderProfilesStorage(orderedIds);
}

export async function removeProfile(profileId: string): Promise<void> {
  await removeProfileStorage(profileId);
}

export async function getProfiles(): Promise<Profile[]> {
  return getProfilesStorage();
}

/**
 * Get enabled apps for the current account.
 */
export async function getEnabledApps(): Promise<string[]> {
  const account = await getCurrentAccount();
  return account?.enabledApps || [];
}

/**
 * Set enabled apps for the current account.
 */
export async function setEnabledApps(apps: string[]): Promise<void> {
  const account = await getCurrentAccount();
  if (!account) {
    throw new Error('No account found');
  }
  const updated: Account = {
    ...account,
    enabledApps: apps,
  };
  await setAccountStorage(updated);
}

/**
 * Get all workspaces.
 */
export async function getAllWorkspaces(): Promise<Workspace[]> {
  return getWorkspacesStorage();
}

/**
 * Get a workspace by ID.
 */
export async function getWorkspace(id: string): Promise<Workspace | null> {
  return getWorkspaceStorage(id);
}

/**
 * Create a new workspace.
 */
export async function createWorkspace(data: {
  name: string;
  profileTypePolicy?: ProfileType[];
  verificationRequirements?: string[];
}): Promise<Workspace> {
  const now = new Date();
  const workspace: Workspace = {
    id: generateId(),
    name: data.name,
    profileTypePolicy: data.profileTypePolicy || [],
    verificationRequirements: data.verificationRequirements || [],
    createdAt: now,
    updatedAt: now,
  };

  await setWorkspaceStorage(workspace);
  return workspace;
}

/**
 * Update a workspace.
 */
export async function updateWorkspace(
  id: string,
  updates: {
    name?: string;
    profileTypePolicy?: ProfileType[];
    verificationRequirements?: string[];
  }
): Promise<Workspace> {
  const workspace = await getWorkspaceStorage(id);
  if (!workspace) {
    throw new Error(`Workspace not found: ${id}`);
  }

  const updated: Workspace = {
    ...workspace,
    ...updates,
    updatedAt: new Date(),
  };

  await setWorkspaceStorage(updated);
  return updated;
}

/**
 * Delete a workspace.
 */
export async function deleteWorkspace(id: string): Promise<void> {
  await removeWorkspaceStorage(id);
  // Note: workspace members are not automatically deleted here
  // They should be cleaned up separately if needed
}

/**
 * Get members of a workspace.
 */
export async function getWorkspaceMembersList(workspaceId: string): Promise<WorkspaceMember[]> {
  return getWorkspaceMembers(workspaceId);
}

/**
 * Add a member to a workspace.
 */
export async function addWorkspaceMember(
  workspaceId: string,
  profileId: string,
  role: string
): Promise<WorkspaceMember> {
  const workspace = await getWorkspaceStorage(workspaceId);
  if (!workspace) {
    throw new Error(`Workspace not found: ${workspaceId}`);
  }

  const profile = await getProfileStorage(profileId);
  if (!profile) {
    throw new Error(`Profile not found: ${profileId}`);
  }

  const now = new Date();
  const member: WorkspaceMember = {
    workspaceId,
    profileId,
    role,
    status: 'active',
    metadata: {},
    createdAt: now,
    updatedAt: now,
  };

  await setWorkspaceMember(member);
  return member;
}

/**
 * Update a workspace membership.
 */
export async function updateWorkspaceMembership(
  workspaceId: string,
  profileId: string,
  updates: {
    role?: string;
    status?: MembershipStatus;
  }
): Promise<WorkspaceMember> {
  const members = await getWorkspaceMembers(workspaceId);
  const member = members.find(m => m.profileId === profileId);
  if (!member) {
    throw new Error(`Membership not found: ${workspaceId}/${profileId}`);
  }

  const updated: WorkspaceMember = {
    ...member,
    ...updates,
    updatedAt: new Date(),
  };

  await setWorkspaceMember(updated);
  return updated;
}

/**
 * Remove a member from a workspace.
 */
export async function removeWorkspaceMember(workspaceId: string, profileId: string): Promise<void> {
  await removeWorkspaceMemberStorage(workspaceId, profileId);
}

